import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { roleLabel } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, Plus, FileEdit, CheckCircle2, Share2, Package,
  Image as ImageIcon, Settings, Users as UsersIcon, LogOut, Menu, Archive, Sparkles,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/create", label: "Create Catalogue", icon: Plus, testid: "nav-create" },
  { to: "/drafts", label: "Draft Catalogues", icon: FileEdit, testid: "nav-drafts" },
  { to: "/ready", label: "Ready Catalogues", icon: CheckCircle2, testid: "nav-ready" },
  { to: "/shared", label: "Shared Catalogues", icon: Share2, testid: "nav-shared" },
  { to: "/archived", label: "Archived", icon: Archive, testid: "nav-archived" },
  { to: "/library", label: "Product Library", icon: Package, testid: "nav-library" },
  { to: "/covers", label: "Cover Designs", icon: ImageIcon, testid: "nav-covers" },
  { to: "/settings", label: "Brand Settings", icon: Settings, admin: true, testid: "nav-settings" },
  { to: "/users", label: "Users & Permissions", icon: UsersIcon, admin: true, testid: "nav-users" },
  { to: "/studio", label: "AI Tea Catalogue Studio", icon: Sparkles, testid: "nav-studio", studio: true },
];

function NavItems({ onNavigate, isAdmin }) {
  return (
    <nav className="flex flex-col gap-1 px-3">
      {NAV.filter((n) => !n.admin || isAdmin).map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onNavigate}
          data-testid={n.testid}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              n.studio ? "mt-4 border border-[#C9A34A]/50 " : ""
            }${
              isActive
                ? "bg-[#C9A34A] text-[#0B3B2E] shadow-sm"
                : "text-[#F7F2E7]/80 hover:bg-white/10 hover:text-[#F7F2E7]"
            }`
          }
        >
          <n.icon className="h-4 w-4 shrink-0" />
          <span>{n.label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarBrand() {
  return (
    <div className="flex items-center gap-3 px-6 py-6 border-b border-[#C9A34A]/25">
      <div className="h-12 w-12 grid place-items-center shrink-0">
        <img src="/pgt-logo-hd.png" alt="Prime Gold Tea" className="h-full w-full object-contain drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]" data-testid="sidebar-logo" />
      </div>
      <div className="leading-tight">
        <div className="font-display text-xl text-[#C9A34A] tracking-wide">Prime Gold Tea</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#F7F2E7]/60">Catalogue Studio</div>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background pgt-paper">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-72 flex-col bg-[#0B3B2E] z-40">
        <SidebarBrand />
        <div className="flex-1 overflow-y-auto py-4">
          <NavItems isAdmin={isAdmin} />
        </div>
        <div className="border-t border-[#C9A34A]/25 p-4">
          <div className="mb-3 rounded-lg bg-white/5 px-3 py-2">
            <div className="text-sm font-semibold text-[#F7F2E7] truncate" data-testid="sidebar-username">{user?.name}</div>
            <div className="text-[11px] text-[#C9A34A]">{roleLabel(user?.role)}</div>
          </div>
          <Button
            variant="ghost"
            onClick={doLogout}
            data-testid="logout-btn"
            className="w-full justify-start gap-2 text-[#F7F2E7]/80 hover:bg-white/10 hover:text-[#F7F2E7]"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:pl-72 flex-1 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between bg-[#0B3B2E] px-4 py-3">
          <div className="flex items-center gap-2">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button size="icon" variant="ghost" data-testid="mobile-menu-btn" className="text-[#F7F2E7] hover:bg-white/10">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-[#0B3B2E] border-none">
                <SidebarBrand />
                <div className="py-4">
                  <NavItems isAdmin={isAdmin} onNavigate={() => setOpen(false)} />
                </div>
                <div className="p-4 border-t border-[#C9A34A]/25">
                  <Button variant="ghost" onClick={doLogout} className="w-full justify-start gap-2 text-[#F7F2E7]/80 hover:bg-white/10">
                    <LogOut className="h-4 w-4" /> Sign out
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
            <span className="flex items-center gap-2">
              <img src="/pgt-logo-hd.png" alt="Prime Gold Tea" className="h-8 w-8 object-contain" data-testid="mobile-header-logo" />
              <span className="font-display text-lg text-[#C9A34A]">Prime Gold Tea</span>
            </span>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
